import React, { useState, useEffect, useCallback } from 'react';

interface TrendDataPoint {
  timestamp: number;
  value: number;
}

interface EconomyTrends {
  currencyTrend: TrendDataPoint[];
  playerCountTrend: TrendDataPoint[];
  botActivityTrend: TrendDataPoint[];
  itemGenerationTrend: TrendDataPoint[];
}

interface EconomyChartsAndTrendsProps {
  apiBaseUrl: string;
  adminKey: string;
}

export const EconomyChartsAndTrends: React.FC<EconomyChartsAndTrendsProps> = ({ apiBaseUrl, adminKey }) => {
  const [trends, setTrends] = useState<EconomyTrends | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [timeRange, setTimeRange] = useState<'1h' | '6h' | '24h' | '7d'>('24h');
  const [autoRefresh, setAutoRefresh] = useState<boolean>(true);

  const fetchTrends = useCallback(async (): Promise<void> => {
    try {
      setIsLoading(true);
      const response = await fetch(`${apiBaseUrl}/api/admin/prod/economy-trends?range=${timeRange}`, {
        headers: { 'x-admin-key': adminKey },
      });
      if (response.ok) {
        const data = await response.json();
        setTrends(data);
      }
    } catch (err) {
      console.error('Error fetching trends:', err);
    } finally {
      setIsLoading(false);
    }
  }, [apiBaseUrl, adminKey, timeRange]);

  useEffect(() => {
    fetchTrends();
    if (!autoRefresh) return;
    const interval = setInterval(() => fetchTrends(), 30000);
    return () => clearInterval(interval);
  }, [fetchTrends, autoRefresh]);

  const renderSimpleChart = (data: TrendDataPoint[], height: number = 120): JSX.Element => {
    if (!data || data.length === 0) {
      return <div style={{ height, color: '#718096', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>No data</div>;
    }

    const values = data.map(d => d.value);
    const minValue = Math.min(...values);
    const maxValue = Math.max(...values);
    const range = maxValue - minValue || 1;

    const chartWidth = Math.max(data.length * 4, 200);
    const barWidth = Math.max(2, chartWidth / data.length);

    return (
      <div style={{
        display: 'flex',
        alignItems: 'flex-end',
        gap: 1,
        height,
        overflow: 'auto',
        backgroundColor: '#0b1220',
        borderRadius: 4,
        padding: 8,
      }}>
        {data.map((point, idx) => {
          const normalizedValue = (point.value - minValue) / range;
          const barHeight = Math.max(2, normalizedValue * (height - 20));
          return (
            <div
              key={idx}
              style={{
                width: barWidth,
                height: barHeight,
                backgroundColor: '#3b82f6',
                borderRadius: 1,
                opacity: 0.7 + normalizedValue * 0.3,
                minWidth: 2,
              }}
              title={`${point.value.toLocaleString()}`}
            />
          );
        })}
      </div>
    );
  };

  const calculateTrend = (data: TrendDataPoint[]): { direction: 'up' | 'down' | 'stable'; percentage: number } => {
    if (!data || data.length < 2) return { direction: 'stable', percentage: 0 };

    const firstHalf = data.slice(0, Math.floor(data.length / 2));
    const secondHalf = data.slice(Math.floor(data.length / 2));

    const avgFirst = firstHalf.reduce((sum, d) => sum + d.value, 0) / firstHalf.length;
    const avgSecond = secondHalf.reduce((sum, d) => sum + d.value, 0) / secondHalf.length;

    const change = ((avgSecond - avgFirst) / avgFirst) * 100;
    const direction = change > 2 ? 'up' : change < -2 ? 'down' : 'stable';

    return { direction, percentage: Math.abs(change) };
  };

  const getTrendColor = (direction: string): string => {
    switch (direction) {
      case 'up':
        return '#22c55e';
      case 'down':
        return '#ef4444';
      default:
        return '#a0aec0';
    }
  };

  const getTrendIcon = (direction: string): string => {
    switch (direction) {
      case 'up':
        return '▲';
      case 'down':
        return '▼';
      default:
        return '→';
    }
  };

  if (!trends) {
    return (
      <div style={{
        backgroundColor: '#1a1a2e',
        border: '1px solid #4a5568',
        borderRadius: 0,
        padding: 16,
        color: '#a0aec0',
        fontSize: 12,
        fontFamily: 'monospace',
      }}>
        LOADING CHART DATA...
      </div>
    );
  }

  const currencyTrend = calculateTrend(trends.currencyTrend);
  const playerTrend = calculateTrend(trends.playerCountTrend);
  const botTrend = calculateTrend(trends.botActivityTrend);
  const itemTrend = calculateTrend(trends.itemGenerationTrend);

  return (
    <div style={{
      backgroundColor: '#1a1a2e',
      border: '1px solid #4a5568',
      borderRadius: 0,
      padding: 16,
      marginBottom: 16,
      fontFamily: 'monospace',
    }}>
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: 16,
      }}>
        <h3 style={{ color: '#ffa500', fontSize: 14, margin: 0, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 1, fontFamily: 'monospace' }}>📊 ECONOMY CHARTS & TRENDS</h3>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <div style={{ display: 'flex', gap: 4 }}>
            {(['1h', '6h', '24h', '7d'] as const).map(range => (
              <button
                key={range}
                onClick={() => setTimeRange(range)}
                style={{
                  padding: '4px 8px',
                  backgroundColor: timeRange === range ? '#ffa500' : '#2d3748',
                  color: timeRange === range ? '#000' : '#e2e8f0',
                  border: 'none',
                  fontSize: 10,
                  cursor: 'pointer',
                  borderRadius: 0,
                  fontWeight: timeRange === range ? 600 : 400,
                  textTransform: 'uppercase',
                  letterSpacing: 0.5,
                }}
              >
                {range}
              </button>
            ))}
          </div>
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, color: '#a0aec0', fontSize: 11, cursor: 'pointer', fontFamily: 'monospace', textTransform: 'uppercase', letterSpacing: 0.5 }}>
            <input
              type="checkbox"
              checked={autoRefresh}
              onChange={(e) => setAutoRefresh(e.target.checked)}
              style={{ cursor: 'pointer' }}
            />
            AUTO-REFRESH
          </label>
          <button
            onClick={() => fetchTrends()}
            disabled={isLoading}
            style={{
              padding: '4px 8px',
              backgroundColor: isLoading ? '#2d3748' : '#4a5568',
              color: isLoading ? '#718096' : '#e2e8f0',
              border: '1px solid #4a5568',
              fontSize: 11,
              cursor: isLoading ? 'default' : 'pointer',
              borderRadius: 2,
            }}
          >
            {isLoading ? 'Loading...' : 'Refresh'}
          </button>
        </div>
      </div>

      {/* Charts Grid */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(2, 1fr)',
        gap: 16,
      }}>
        {/* Currency Trend */}
        <div style={{
          backgroundColor: '#0b1220',
          border: '1px solid #4a5568',
          borderRadius: 0,
          padding: 12,
        }}>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: 8,
          }}>
            <div style={{ color: '#ffa500', fontSize: 12, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>CURRENCY IN CIRCULATION</div>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: 4,
              color: getTrendColor(currencyTrend.direction),
              fontSize: 11,
              fontWeight: 600,
            }}>
              {getTrendIcon(currencyTrend.direction)} {currencyTrend.percentage.toFixed(1)}%
            </div>
          </div>
          {renderSimpleChart(trends.currencyTrend, 100)}
          <div style={{ marginTop: 8, fontSize: 10, color: '#a0aec0' }}>
            Current: ₱{trends.currencyTrend[trends.currencyTrend.length - 1]?.value.toLocaleString() || 'N/A'}
          </div>
        </div>

        {/* Player Count Trend */}
        <div style={{
          backgroundColor: '#0b1220',
          border: '1px solid #4a5568',
          borderRadius: 0,
          padding: 12,
        }}>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: 8,
          }}>
            <div style={{ color: '#ffa500', fontSize: 12, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>👥 PLAYER COUNT</div>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: 4,
              color: getTrendColor(playerTrend.direction),
              fontSize: 11,
              fontWeight: 600,
            }}>
              {getTrendIcon(playerTrend.direction)} {playerTrend.percentage.toFixed(1)}%
            </div>
          </div>
          {renderSimpleChart(trends.playerCountTrend, 100)}
          <div style={{ marginTop: 8, fontSize: 10, color: '#a0aec0' }}>
            Current: {trends.playerCountTrend[trends.playerCountTrend.length - 1]?.value || 'N/A'} players
          </div>
        </div>

        {/* Bot Activity Trend */}
        <div style={{
          backgroundColor: '#0b1220',
          border: '1px solid #4a5568',
          borderRadius: 0,
          padding: 12,
        }}>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: 8,
          }}>
            <div style={{ color: '#ffa500', fontSize: 12, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>🤖 BOT ACTIVITY</div>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: 4,
              color: getTrendColor(botTrend.direction),
              fontSize: 11,
              fontWeight: 600,
            }}>
              {getTrendIcon(botTrend.direction)} {botTrend.percentage.toFixed(1)}%
            </div>
          </div>
          {renderSimpleChart(trends.botActivityTrend, 100)}
          <div style={{ marginTop: 8, fontSize: 10, color: '#a0aec0' }}>
            Current: {trends.botActivityTrend[trends.botActivityTrend.length - 1]?.value || 'N/A'} orders/tick
          </div>
        </div>

        {/* Item Generation Trend */}
        <div style={{
          backgroundColor: '#0b1220',
          border: '1px solid #4a5568',
          borderRadius: 0,
          padding: 12,
        }}>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: 8,
          }}>
            <div style={{ color: '#ffa500', fontSize: 12, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>ITEM GENERATION</div>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: 4,
              color: getTrendColor(itemTrend.direction),
              fontSize: 11,
              fontWeight: 600,
            }}>
              {getTrendIcon(itemTrend.direction)} {itemTrend.percentage.toFixed(1)}%
            </div>
          </div>
          {renderSimpleChart(trends.itemGenerationTrend, 100)}
          <div style={{ marginTop: 8, fontSize: 10, color: '#a0aec0' }}>
            Current: {trends.itemGenerationTrend[trends.itemGenerationTrend.length - 1]?.value || 'N/A'} items/tick
          </div>
        </div>
      </div>

      {/* Summary Stats */}
      <div style={{
        marginTop: 16,
        padding: 12,
        backgroundColor: '#0b1220',
        borderRadius: 0,
        display: 'grid',
        gridTemplateColumns: 'repeat(4, 1fr)',
        gap: 12,
      }}>
        <div>
          <div style={{ color: '#a0aec0', fontSize: 10, marginBottom: 4 }}>Currency Trend</div>
          <div style={{ color: getTrendColor(currencyTrend.direction), fontSize: 12, fontWeight: 600 }}>
            {getTrendIcon(currencyTrend.direction)} {currencyTrend.direction.toUpperCase()}
          </div>
        </div>
        <div>
          <div style={{ color: '#a0aec0', fontSize: 10, marginBottom: 4 }}>Player Trend</div>
          <div style={{ color: getTrendColor(playerTrend.direction), fontSize: 12, fontWeight: 600 }}>
            {getTrendIcon(playerTrend.direction)} {playerTrend.direction.toUpperCase()}
          </div>
        </div>
        <div>
          <div style={{ color: '#a0aec0', fontSize: 10, marginBottom: 4 }}>Bot Activity Trend</div>
          <div style={{ color: getTrendColor(botTrend.direction), fontSize: 12, fontWeight: 600 }}>
            {getTrendIcon(botTrend.direction)} {botTrend.direction.toUpperCase()}
          </div>
        </div>
        <div>
          <div style={{ color: '#a0aec0', fontSize: 10, marginBottom: 4 }}>Item Generation Trend</div>
          <div style={{ color: getTrendColor(itemTrend.direction), fontSize: 12, fontWeight: 600 }}>
            {getTrendIcon(itemTrend.direction)} {itemTrend.direction.toUpperCase()}
          </div>
        </div>
      </div>
    </div>
  );
};
